import React, {useEffect} from "react";
import type {ReactNode} from "react";
import {Button, Select, Tag, Tooltip, Alert} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import type {UseFormHandleSubmit} from "react-hook-form";
import {
  StyledCreateAutomationDrawer,
  StyledOfferingDrawerAutomationTypeBlock,
  StyledOfferingDrawerAutomationTypeLabel,
  StyledOfferingDrawerAutomationTypeHelper,
  StyledOfferingDrawerAutomationReadonlyInput,
  StyledOfferingDrawerAutomationPlaceholder,
  StyledOfferingDrawerAutomationTypeOptionInner,
  OfferingDrawerAutomationTypeDropdownStyles,
} from "../../automations.style";
import {CreateAutomationForm} from "./CreateAutomationForm";
import {EmailPreview} from "./EmailPreview";
import {ShortCodesList} from "./ShortCodesList";
import type {CreateAutomationFormValues} from "./schema";

export type DrawerView = "form" | "shortcodes" | "preview";

export type OfferingsDrawerAutomationKind = "email" | "file_delivery";

export interface OfferingsFlowDrawerConfig {
  automationKind: OfferingsDrawerAutomationKind;
  onAutomationKindChange: (kind: OfferingsDrawerAutomationKind) => void;
  /**
   * Create flow only: enables "Create and add" when this is true (parent should derive from full publish schema).
   */
  createFormMeetsPublishSchema?: boolean;
  /**
   * Create flow: messages from `createAutomationSchema.validateSync` when gate is failing.
   * Used in the disabled "Create and add" tooltip.
   */
  createFormGateMessages?: string[];
  /**
   * Edit flow: linked offering count from API pivot (e.g. `normalizeEmailAutomation` → `offerings`).
   * When greater than 1, a shared-automation warning is shown.
   */
  editLinkedOfferingCount?: number;
}

interface CreateAutomationDrawerProps {
  visible: boolean;
  isEditMode?: boolean;
  onClose: () => void;
  currentView: DrawerView;
  onViewChange: (view: DrawerView) => void;
  formProps: React.ComponentProps<typeof CreateAutomationForm>;
  handleSubmit: UseFormHandleSubmit<CreateAutomationFormValues>;
  watch: (name: keyof CreateAutomationFormValues) => unknown;
  onSubmit: (values: CreateAutomationFormValues) => void;
  onSaveDraft: (values: CreateAutomationFormValues) => void;
  onSaveDraftClick: () => void;
  onInsertShortCode: (code: string) => void;
  onDeleteAndClose: () => void;
  isSubmitting?: boolean;
  /** When set, offerings flow: titles, automation type row on create, conditional body. */
  offeringsFlow?: OfferingsFlowDrawerConfig;
}

const CREATE_TITLE = "Create an email automation";
const EDIT_TITLE = "Edit email automation";
const OFFERINGS_CREATE_TITLE = "Create new automation";
const OFFERINGS_EDIT_TITLE = "Edit automation";

const {Option} = Select;

const AUTOMATION_TYPE_DROPDOWN_CLASS = "offering-drawer-automation-type-dropdown";

const OFFERINGS_CREATE_AND_ADD_DISABLED_TOOLTIP =
  "Complete all required fields before creating this automation.";

const FILE_DELIVERY_DRAWER_DISABLED_TOOLTIP =
  "File delivery automations are not available yet.";

function offeringsCreateAndAddDisabledTooltipTitle(
  gated: boolean,
  gateMessages: string[] | undefined
): ReactNode | undefined {
  if (!gated) return undefined;
  const cleaned = (gateMessages ?? []).map((m) => m.trim()).filter(Boolean);
  if (cleaned.length === 0) return OFFERINGS_CREATE_AND_ADD_DISABLED_TOOLTIP;
  return (
    <div>
      <div style={{fontWeight: 600, marginBottom: 8}}>Complete the following:</div>
      <ul style={{margin: 0, paddingLeft: 18}}>
        {cleaned.map((msg, idx) => (
          <li key={idx} style={{marginBottom: 4}}>
            {msg}
          </li>
        ))}
      </ul>
    </div>
  );
}

function offeringsAutomationKindLabel(kind: OfferingsDrawerAutomationKind): string {
  if (kind === "file_delivery") return "File delivery";
  return "Email";
}

export function CreateAutomationDrawer({
  visible,
  isEditMode = false,
  onClose,
  currentView,
  onViewChange,
  formProps,
  handleSubmit,
  watch,
  onSubmit,
  onSaveDraft,
  onSaveDraftClick,
  onInsertShortCode,
  onDeleteAndClose,
  isSubmitting = false,
  offeringsFlow,
}: CreateAutomationDrawerProps) {
  const subject = (watch("subject") as string) || "";
  const body = (watch("body") as string) || "";
  const buttonText = (watch("buttonText") as string) || "";
  const addCustomButton = (watch("addCustomButton") as boolean) ?? false;

  const offeringsActive = offeringsFlow != null;
  const automationKind = offeringsFlow?.automationKind ?? "email";

  const formTitle =
    offeringsActive && isEditMode
      ? OFFERINGS_EDIT_TITLE
      : offeringsActive && !isEditMode
      ? OFFERINGS_CREATE_TITLE
      : isEditMode
      ? EDIT_TITLE
      : CREATE_TITLE;

  const showEmailFormBody = !offeringsActive || isEditMode || automationKind === "email";

  useEffect(() => {
    if (
      offeringsActive &&
      !isEditMode &&
      automationKind !== "email" &&
      currentView !== "form"
    ) {
      onViewChange("form");
    }
  }, [offeringsActive, isEditMode, automationKind, currentView, onViewChange]);

  const showOfferingsAutomationTypeRow =
    offeringsActive && !isEditMode && currentView === "form";

  const showOfferingsAutomationTypeReadOnly =
    offeringsActive && isEditMode && currentView === "form";

  const showFooter = currentView === "form" && showEmailFormBody;

  const offeringsCreatePublishReady =
    offeringsFlow?.createFormMeetsPublishSchema === true;

  /** Disabled primary keeps dark bg with washed-out text — use default ghost for readable gate UX. */
  const offeringsCreateGatedInactive =
    offeringsActive && !isEditMode && !offeringsCreatePublishReady && !isSubmitting;

  const editLinkedOfferingCount = offeringsFlow?.editLinkedOfferingCount ?? 0;
  const showOfferingsEditMultiOfferingWarning =
    offeringsActive && isEditMode && editLinkedOfferingCount > 1;

  const offeringsEditTitleBlock = showOfferingsEditMultiOfferingWarning ? (
    <div>
      <div style={{fontSize: 18, fontWeight: 600, color: "#1c1726"}}>
        {OFFERINGS_EDIT_TITLE}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 14,
          fontWeight: 400,
          color: "#595959",
          lineHeight: 1.45,
        }}
      >
        Changes apply everywhere this automation is used.
      </div>
    </div>
  ) : null;

  const handleDrawerClose = () => {
    if (currentView === "shortcodes" || currentView === "preview") {
      onViewChange("form");
      return;
    }
    onClose();
  };

  return (
    <>
      {offeringsFlow ? <OfferingDrawerAutomationTypeDropdownStyles /> : null}
      <StyledCreateAutomationDrawer
        title={
          currentView === "form" ? (
            offeringsEditTitleBlock ? (
              offeringsEditTitleBlock
            ) : (
              formTitle
            )
          ) : (
            <span>
              <span
                style={{cursor: "pointer", color: "#595959"}}
                onClick={() => onViewChange("form")}
              >
                {offeringsActive && isEditMode ? OFFERINGS_EDIT_TITLE : formTitle}
              </span>
              <span style={{margin: "0 8px", color: "#595959"}}>/</span>
              <span style={{fontWeight: 400}}>
                {currentView === "shortcodes" ? "Insert short codes" : "Preview"}
              </span>
            </span>
          )
        }
        placement="right"
        onClose={handleDrawerClose}
        visible={visible}
        closable
      >
        <div style={{paddingBottom: "80px"}}>
          <div style={{display: currentView === "form" ? "block" : "none"}}>
            {showOfferingsEditMultiOfferingWarning ? (
              <Alert
                type="warning"
                showIcon
                message={`Shared across ${editLinkedOfferingCount} offerings`}
                description="Changes will apply everywhere this automation is used."
                style={{
                  marginBottom: 20,
                  borderRadius: 12,
                }}
              />
            ) : null}
            {showOfferingsAutomationTypeReadOnly && offeringsFlow ? (
              <StyledOfferingDrawerAutomationTypeBlock>
                <StyledOfferingDrawerAutomationTypeLabel>
                  Automation type
                </StyledOfferingDrawerAutomationTypeLabel>
                <StyledOfferingDrawerAutomationReadonlyInput
                  aria-readonly="true"
                  disabled
                  value={offeringsAutomationKindLabel(automationKind)}
                />
                <StyledOfferingDrawerAutomationTypeHelper>
                  Type cannot be changed after creation
                </StyledOfferingDrawerAutomationTypeHelper>
              </StyledOfferingDrawerAutomationTypeBlock>
            ) : null}
            {showOfferingsAutomationTypeRow && offeringsFlow ? (
              <StyledOfferingDrawerAutomationTypeBlock>
                <StyledOfferingDrawerAutomationTypeLabel>
                  Automation type
                </StyledOfferingDrawerAutomationTypeLabel>
                <Select
                  className="offering-drawer-automation-type-select"
                  dropdownClassName={AUTOMATION_TYPE_DROPDOWN_CLASS}
                  bordered
                  value={offeringsFlow.automationKind}
                  onChange={(v) =>
                    offeringsFlow.onAutomationKindChange(
                      v as OfferingsDrawerAutomationKind
                    )
                  }
                  optionLabelProp="label"
                >
                  <Option value="email" label="Email">
                    <StyledOfferingDrawerAutomationTypeOptionInner $disabled={false}>
                      <div className="option-text">
                        <span className="option-title">Email</span>
                        <span className="option-desc">
                          Send a branded email when triggered
                        </span>
                      </div>
                      <span className="option-selected-check" aria-hidden>
                        ✓
                      </span>
                    </StyledOfferingDrawerAutomationTypeOptionInner>
                  </Option>
                  <Option
                    value="file_delivery"
                    disabled
                    label="File delivery"
                    title={FILE_DELIVERY_DRAWER_DISABLED_TOOLTIP}
                  >
                    <StyledOfferingDrawerAutomationTypeOptionInner $disabled>
                      <div className="option-text">
                        <span className="option-title">
                          File delivery{" "}
                          <Tag color="#d9d9d9" style={{marginLeft: 8}}>
                            SOON
                          </Tag>
                        </span>
                        <span className="option-desc">Deliver a file or download</span>
                      </div>
                    </StyledOfferingDrawerAutomationTypeOptionInner>
                  </Option>
                </Select>
              </StyledOfferingDrawerAutomationTypeBlock>
            ) : null}
            {showEmailFormBody ? (
              <CreateAutomationForm
                {...formProps}
                hideOfferingsAssignmentField={offeringsActive}
              />
            ) : (
              <StyledOfferingDrawerAutomationPlaceholder>
                This automation type isn&apos;t available yet.
              </StyledOfferingDrawerAutomationPlaceholder>
            )}
          </div>
          <div style={{display: currentView === "shortcodes" ? "block" : "none"}}>
            <ShortCodesList onInsertShortCode={onInsertShortCode} />
          </div>
          <div style={{display: currentView === "preview" ? "block" : "none"}}>
            <EmailPreview
              subject={subject}
              body={body}
              buttonText={buttonText}
              addCustomButton={addCustomButton}
            />
          </div>
        </div>
        {showFooter ? (
          <div
            className="drawer-footer"
            style={offeringsActive ? {justifyContent: "space-between"} : undefined}
          >
            {offeringsActive && isEditMode ? (
              <>
                <Button
                  style={{
                    borderRadius: "12px",
                    height: "44px",
                    padding: "0 24px",
                    fontWeight: 600,
                    color: "#1c1726",
                    borderColor: "#1c1726",
                  }}
                  disabled={isSubmitting}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  style={{
                    background: "#1c1726",
                    borderRadius: "12px",
                    height: "44px",
                    padding: "0 24px",
                    fontWeight: 600,
                  }}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                >
                  Save changes
                </Button>
              </>
            ) : offeringsActive ? (
              <>
                <Button
                  style={{
                    borderRadius: "12px",
                    height: "44px",
                    padding: "0 24px",
                    fontWeight: 600,
                    color: "#1c1726",
                    borderColor: "#1c1726",
                  }}
                  disabled={isSubmitting}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Tooltip
                  title={offeringsCreateAndAddDisabledTooltipTitle(
                    offeringsCreateGatedInactive,
                    offeringsFlow?.createFormGateMessages
                  )}
                  overlayInnerStyle={{maxWidth: 360}}
                >
                  <span style={{display: "inline-block"}}>
                    <Button
                      type={offeringsCreateGatedInactive ? "default" : "primary"}
                      style={{
                        borderRadius: "12px",
                        height: "44px",
                        padding: "0 24px",
                        fontWeight: 600,
                        ...(offeringsCreateGatedInactive
                          ? {}
                          : {
                              background: "#1c1726",
                              borderColor: "#1c1726",
                              color: "#fff",
                            }),
                      }}
                      loading={isSubmitting}
                      disabled={offeringsCreateGatedInactive}
                      onClick={handleSubmit(onSubmit)}
                    >
                      Create and add
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  type="primary"
                  style={{
                    background: "#1c1726",
                    borderRadius: "12px",
                    height: "44px",
                    padding: "0 24px",
                    fontWeight: 600,
                  }}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                >
                  Publish
                </Button>
                <Button
                  style={{
                    borderRadius: "12px",
                    height: "44px",
                    padding: "0 24px",
                    fontWeight: 600,
                    color: "#1c1726",
                    borderColor: "#1c1726",
                  }}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={onSaveDraftClick}
                >
                  Save as draft
                </Button>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  style={{
                    color: "#ff4d4f",
                    height: "44px",
                    width: "44px",
                    marginLeft: "auto",
                  }}
                  onClick={onDeleteAndClose}
                />
              </>
            )}
          </div>
        ) : null}
      </StyledCreateAutomationDrawer>
    </>
  );
}
